// YearRangeDatePicker.js - Reusable Component
import React, { useState } from 'react';
import { Grid, TextField, Button, Paper, Typography, Box, IconButton } from '@mui/material';
import { ArrowBack, CalendarToday } from '@mui/icons-material';

const YearRangeDatePicker = ({ 
  label = "Date of Birth", 
  name = "date_of_birth",
  value = "",
  onChange,
  required = false,
  maxDate = new Date(),
  minDate = new Date('1900-01-01'),
  placeholder = "Select date",
  ...props 
}) => {
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Generate year ranges dynamically based on min/max dates
  const generateYearRanges = () => {
    const currentYear = new Date().getFullYear();
    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();
    
    const ranges = [];
    
    // Create ranges of 20 years each
    for (let start = maxYear; start >= minYear; start -= 20) {
      const end = Math.max(start - 19, minYear);
      ranges.push({
        label: `${end}-${start}`,
        start: end,
        end: start
      });
    }
    
    return ranges;
  };

  const yearRanges = generateYearRanges();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleYearRangeClick = (range) => {
    setSelectedRange(range);
  };

  const handleYearClick = (year) => {
    setSelectedYear(year);
  };

  const handleMonthClick = (month, monthIndex) => {
    setSelectedMonth({ name: month, index: monthIndex });
  };

  // Helper function to check if a date is valid (not in future)
  const isValidDate = (year, month, day) => {
    const selectedDate = new Date(year, month, day);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today
    return selectedDate <= today;
  };

  // Filter months to show only valid months
  const getValidMonths = () => {
    if (!selectedYear) return months;
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    
    if (selectedYear === currentYear) {
      // If current year, only show months up to current month
      return months.slice(0, currentMonth + 1);
    } else if (selectedYear < currentYear) {
      // If past year, show all months
      return months;
    } else {
      // If future year, show no months (shouldn't happen with proper year filtering)
      return [];
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    
    // Format the date as YYYY-MM-DD
    const formattedDate = `${selectedYear}-${String(selectedMonth.index + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Call the parent's onChange function
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: formattedDate
        }
      });
    }
    
    // Close the picker
    setShowYearPicker(false);
    setSelectedRange(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
  };

  const generateYearsInRange = (start, end) => {
    const years = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = end; year >= start; year--) {
      // Only add years that are not in the future
      if (year <= currentYear) {
        years.push(year);
      }
    }
    return years;
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const generateDays = () => {
    if (!selectedYear || selectedMonth === null) return [];
    
    const daysCount = getDaysInMonth(selectedYear, selectedMonth.index);
    const days = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentDay = new Date().getDate();
    
    for (let day = 1; day <= daysCount; day++) {
      // Only add days that are not in the future
      if (selectedYear === currentYear && selectedMonth.index === currentMonth) {
        // If current year and current month, only show days up to today
        if (day <= currentDay) {
          days.push(day);
        }
      } else if (selectedYear < currentYear || 
                (selectedYear === currentYear && selectedMonth.index < currentMonth)) {
        // If past year or past month in current year, show all days
        days.push(day);
      }
      // If future year or future month, don't add any days (shouldn't happen with proper filtering)
    }
    return days;
  };

  const handleBackClick = () => {
    if (selectedDay) {
      setSelectedDay(null);
    } else if (selectedMonth) {
      setSelectedMonth(null);
    } else if (selectedYear) {
      setSelectedYear(null);
    } else if (selectedRange) {
      setSelectedRange(null);
    } else {
      setShowYearPicker(false);
    }
  };

  const handleTextFieldClick = () => {
    setShowYearPicker(true);
  };

  return (
    <Box sx={{ position: 'relative' }} {...props}>
      <TextField
        label={label}
        value={value}
        name={name}
        onClick={handleTextFieldClick}
        required={required}
        placeholder={placeholder}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: <CalendarToday sx={{ color: 'action.active' }} />
        }}
        sx={{ cursor: 'pointer' }}
      />

      {showYearPicker && (
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            p: 2, 
            maxHeight: 400, 
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            zIndex: 1000
          }}
        >
          {/* Header with back button and title */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {(selectedRange || selectedYear || selectedMonth) && (
              <IconButton onClick={handleBackClick} size="small">
                <ArrowBack />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {!selectedRange && 'Select Year Range'}
              {selectedRange && !selectedYear && `Years ${selectedRange.label}`}
              {selectedYear && !selectedMonth && `Months for ${selectedYear}`}
              {selectedMonth && `Days in ${selectedMonth.name} ${selectedYear}`}
            </Typography>
          </Box>

          {/* Year Range Selection */}
          {!selectedRange && (
            <Grid container spacing={1}>
              {yearRanges.map((range) => (
                <Grid item xs={6} sm={4} key={range.label}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleYearRangeClick(range)}
                    sx={{ 
                      height: '60px',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {range.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Year Selection */}
          {selectedRange && !selectedYear && (
            <Grid container spacing={1}>
              {generateYearsInRange(selectedRange.start, selectedRange.end).map((year) => (
                <Grid item xs={3} sm={2} key={year}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleYearClick(year)}
                    sx={{ 
                      height: '50px',
                      fontSize: '12px'
                    }}
                  >
                    {year}
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Month Selection */}
          {selectedYear && !selectedMonth && (
            <Grid container spacing={1}>
              {getValidMonths().map((month, index) => (
                <Grid item xs={4} sm={3} key={month}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleMonthClick(month, index)}
                    sx={{ 
                      height: '50px',
                      fontSize: '12px'
                    }}
                  >
                    {month.substring(0, 3)}
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Day Selection */}
          {selectedMonth && (
            <Grid container spacing={1}>
              {generateDays().map((day) => (
                <Grid item xs={2} sm={1.5} key={day}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleDayClick(day)}
                    sx={{ 
                      height: '40px',
                      fontSize: '12px',
                      minWidth: '40px'
                    }}
                  >
                    {day}
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default YearRangeDatePicker;

